import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { IconArrowRight } from '@/components/icons/Icons'

/*
  RelationCell — una celda que nombra UNA relación y ofrece el resto.

  Una empresa casi nunca tiene un contacto, y un contacto puede estar en varias
  empresas. Mostrar solo el primero miente (y mentía: la tabla de Empresas
  enseñaba al primero que devolvía la consulta, no al principal), y mostrarlos
  todos revienta el ancho de la columna. Entonces: el principal a la vista, y un
  chip «+N» que abre la lista entera con un enlace a cada uno.

  El panel va por portal a <body> y con posición fija a propósito: la tabla vive
  dentro de `.table-container`, que recorta lo que se sale — un popover normal
  quedaría cortado por el borde de la tabla. Por eso también se cierra al hacer
  scroll: el ancla se movió y el panel se quedaría flotando donde ya no está.
*/

export interface RelationItem {
  id: number | string
  /** Lo que se lee: el nombre de la persona o de la empresa. */
  label: string
  /** Segunda línea: el email, el cargo, el RUC. */
  sub?: string | null
  /** Etiquetas cortas de la relación (los roles del contacto en la empresa). */
  badges?: string[]
  /** Destino de la ficha. Sin esto el item se lista pero no es enlace. */
  to?: string
  /** El principal: el que se muestra en la celda y el que se marca en la lista. */
  principal?: boolean
}

interface RelationCellProps {
  items: RelationItem[]
  /** Encabezado del panel, p. ej. «Contactos de LibreOvo». */
  titulo: string
  /** Singular/plural para el aria-label del chip: ['contacto', 'contactos']. */
  nombre: [string, string]
  icono?: ReactNode
  /** Qué se dibuja cuando no hay ninguna relación. */
  vacio?: string
}

const ANCHO_PANEL = 280

export function RelationCell({ items, titulo, nombre, icono, vacio = '\u2014' }: RelationCellProps) {
  const [abierto, setAbierto] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const chipRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // La posición se mide después de pintar el panel: antes no se conoce su alto,
  // y sin el alto no se puede decidir si cabe hacia abajo.
  useLayoutEffect(() => {
    if (!abierto || !chipRef.current) return
    const r = chipRef.current.getBoundingClientRect()
    const alto = panelRef.current?.offsetHeight ?? 220
    const cabeAbajo = r.bottom + alto + 8 < window.innerHeight
    setPos({
      top: cabeAbajo ? r.bottom + 6 : Math.max(8, r.top - alto - 6),
      left: Math.min(r.left, window.innerWidth - ANCHO_PANEL - 12),
    })
  }, [abierto])

  useEffect(() => {
    if (!abierto) return
    const cerrar = () => setAbierto(false)
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t) || chipRef.current?.contains(t)) return
      setAbierto(false)
    }
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', tecla)
    // `true` para capturar también el scroll de la tabla, que no burbujea.
    window.addEventListener('scroll', cerrar, true)
    window.addEventListener('resize', cerrar)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', tecla)
      window.removeEventListener('scroll', cerrar, true)
      window.removeEventListener('resize', cerrar)
    }
  }, [abierto])

  if (items.length === 0) return <span className="cell-soft">{vacio}</span>

  const principal = items.find(i => i.principal) ?? items[0]
  const resto = items.length - 1

  return (
    <div className="relation-cell">
      {principal.to
        ? <Link to={principal.to} className="relation-cell__principal">{principal.label}</Link>
        : <span className="relation-cell__principal">{principal.label}</span>}

      {resto > 0 && (
        <button
          ref={chipRef}
          type="button"
          className={`relation-cell__chip${abierto ? ' is-open' : ''}`}
          aria-haspopup="true"
          aria-expanded={abierto}
          aria-label={`Ver los ${items.length} ${nombre[1]}`}
          onClick={e => { e.stopPropagation(); setAbierto(v => !v) }}
        >
          +{resto}
        </button>
      )}

      {abierto && createPortal(
        <div
          ref={panelRef}
          className="relation-pop"
          role="dialog"
          aria-label={titulo}
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: ANCHO_PANEL }}
        >
          <div className="relation-pop__head">
            {icono}
            <span>{titulo}</span>
            <span className="relation-pop__count">{items.length}</span>
          </div>
          <ul className="relation-pop__list">
            {items.map(item => {
              const cuerpo = (
                <>
                  <span className="relation-pop__texto">
                    <span className="relation-pop__label">
                      {item.label}
                      {item.principal && <span className="relation-pop__tag">Principal</span>}
                    </span>
                    {item.sub && <span className="relation-pop__sub">{item.sub}</span>}
                    {item.badges && item.badges.length > 0 && (
                      <span className="relation-pop__badges">
                        {item.badges.map(b => <span key={b} className="relation-pop__badge">{b}</span>)}
                      </span>
                    )}
                  </span>
                  {item.to && <IconArrowRight size={14} className="relation-pop__arrow" />}
                </>
              )
              return (
                <li key={item.id}>
                  {item.to
                    ? <Link to={item.to} className="relation-pop__item" onClick={() => setAbierto(false)}>{cuerpo}</Link>
                    : <span className="relation-pop__item is-static">{cuerpo}</span>}
                </li>
              )
            })}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  )
}
